import { create } from 'zustand';
import {
  getConversations,
  getConversation,
  getAccountConversationCounts,
} from '@/lib/whatsapp-api';
import type {
  WhatsAppConversation,
  WhatsAppMessage,
  ConversationStatus,
  MessageStatus,
} from '@/lib/whatsapp-api';

// =============================================================================
// Types
// =============================================================================

interface ConversationCache {
  conversations: WhatsAppConversation[];
  statusFilter: ConversationStatus | 'all';
  statusCounts: { all: number; open: number; pending: number; resolved: number };
  isLoading: boolean;
  lastFetched: number | null;
  currentPage: number;
  lastPage: number;
  total: number;
  dateFrom: string | null;
  dateTo: string | null;
  agentId: string | null;
  hasUnread: boolean;
  assignedToMe: boolean;
  // Phase 48 INBOX-02 — gated on whatsapp.view_unassigned permission.
  // Mirrors the per-account filter cache shape; sibling of hasUnread.
  withoutAgent?: boolean;
}

interface InboxState {
  // Per-account conversation caches (keyed by account ID)
  accountCaches: Record<string, ConversationCache>;
  // Currently selected account tab
  activeAccountId: string | null;
  // Per-account unread counts (for tab badges)
  accountUnreadCounts: Record<string, number>;

  // Actions
  setActiveAccount: (accountId: string) => void;
  fetchConversations: (accountId: string, options?: { force?: boolean; page?: number; search?: string }) => Promise<void>;
  setStatusFilter: (accountId: string, status: ConversationStatus | 'all') => void;
  setFilters: (accountId: string, filters: Partial<Pick<ConversationCache, 'dateFrom' | 'dateTo' | 'agentId' | 'hasUnread' | 'withoutAgent'>>) => void;
  clearFilters: (accountId: string) => void;
  toggleAssignedToMe: (accountId: string) => void;
  fetchAccountCounts: () => Promise<void>;
  resetStore: () => void;
  // Phase 48 INBOX-03 — optimistic clear on conversation open.
  decrementAccountUnread: (accountId: string, by: number) => void;

  // Socket event handlers
  onMessageReceived: (accountId: string, conversationId: string, message: WhatsAppMessage) => void;
  onMessageStatusUpdate: (accountId: string, whatsappMessageId: string, status: MessageStatus, dbMessageId?: string | number) => void;
  onConversationUpdated: (accountId: string, conversationId: string, updates: Partial<WhatsAppConversation>) => void;
}

// =============================================================================
// Constants
// =============================================================================

const CACHE_STALENESS_MS = 300_000; // 5 minutes

// =============================================================================
// Helpers
// =============================================================================

function createDefaultCache(): ConversationCache {
  return {
    conversations: [],
    statusFilter: 'all',
    statusCounts: { all: 0, open: 0, pending: 0, resolved: 0 },
    isLoading: false,
    lastFetched: null,
    currentPage: 1,
    lastPage: 1,
    total: 0,
    dateFrom: null,
    dateTo: null,
    agentId: null,
    hasUnread: false,
    assignedToMe: false,
    withoutAgent: false,
  };
}

function getOrCreateCache(caches: Record<string, ConversationCache>, accountId: string): ConversationCache {
  return caches[accountId] ?? createDefaultCache();
}

// =============================================================================
// Store
// =============================================================================

export const useInboxStore = create<InboxState>((set, get) => ({
  accountCaches: {},
  activeAccountId: null,
  accountUnreadCounts: {},

  setActiveAccount: (accountId: string) => {
    set({ activeAccountId: accountId });

    const cache = getOrCreateCache(get().accountCaches, accountId);
    const isStale = !cache.lastFetched || Date.now() - cache.lastFetched > CACHE_STALENESS_MS;

    if (cache.conversations.length === 0 || isStale) {
      get().fetchConversations(accountId);
    }
  },

  fetchConversations: async (accountId, options = {}) => {
    const { force = false, page = 1, search } = options;
    const state = get();
    const cache = getOrCreateCache(state.accountCaches, accountId);

    // Set loading state
    set({
      accountCaches: {
        ...state.accountCaches,
        [accountId]: { ...cache, isLoading: true },
      },
    });

    try {
      const statusFilter = cache.statusFilter === 'all' ? undefined : cache.statusFilter;
      const result = await getConversations({
        account_id: accountId,
        status: statusFilter,
        page,
        search,
        date_from: cache.dateFrom ?? undefined,
        date_to: cache.dateTo ?? undefined,
        agent_id: cache.agentId ?? undefined,
        has_unread: cache.hasUnread || undefined,
        assigned_to_me: cache.assignedToMe || undefined,
        without_agent: cache.withoutAgent || undefined,
      });

      set((prev) => {
        const prevCache = getOrCreateCache(prev.accountCaches, accountId);
        let conversations: WhatsAppConversation[];

        if (force || page === 1) {
          // Replace cache entirely
          conversations = result.data;
        } else {
          // Append for pagination (page > 1) — merge by ID, prefer newer
          const existingMap = new Map(prevCache.conversations.map((c) => [c.id, c]));
          for (const conv of result.data) {
            const existing = existingMap.get(conv.id);
            if (!existing || new Date(conv.updated_at) >= new Date(existing.updated_at)) {
              existingMap.set(conv.id, conv);
            }
          }
          conversations = Array.from(existingMap.values());
        }

        return {
          accountCaches: {
            ...prev.accountCaches,
            [accountId]: {
              ...prevCache,
              conversations,
              isLoading: false,
              lastFetched: Date.now(),
              currentPage: result.meta.current_page,
              lastPage: result.meta.last_page,
              total: result.meta.total,
            },
          },
        };
      });
    } catch (error) {
      console.error(`[InboxStore] Failed to fetch conversations for account ${accountId}:`, error);
      set((prev) => ({
        accountCaches: {
          ...prev.accountCaches,
          [accountId]: {
            ...getOrCreateCache(prev.accountCaches, accountId),
            isLoading: false,
          },
        },
      }));
    }
  },

  setStatusFilter: (accountId, status) => {
    const state = get();
    const cache = getOrCreateCache(state.accountCaches, accountId);

    set({
      accountCaches: {
        ...state.accountCaches,
        [accountId]: { ...cache, statusFilter: status },
      },
    });

    // Reload with the new filter
    get().fetchConversations(accountId, { force: true });
  },

  setFilters: (accountId, filters) => {
    const state = get();
    const cache = getOrCreateCache(state.accountCaches, accountId);

    set({
      accountCaches: {
        ...state.accountCaches,
        [accountId]: { ...cache, ...filters },
      },
    });

    // Reload with updated filters (resets to page 1)
    get().fetchConversations(accountId, { force: true });
  },

  clearFilters: (accountId) => {
    const state = get();
    const cache = getOrCreateCache(state.accountCaches, accountId);

    set({
      accountCaches: {
        ...state.accountCaches,
        [accountId]: { ...cache, dateFrom: null, dateTo: null, agentId: null, hasUnread: false, assignedToMe: false, withoutAgent: false },
      },
    });

    // Reload with cleared filters (resets to page 1)
    get().fetchConversations(accountId, { force: true });
  },

  toggleAssignedToMe: (accountId) => {
    const state = get();
    const cache = getOrCreateCache(state.accountCaches, accountId);

    set({
      accountCaches: {
        ...state.accountCaches,
        [accountId]: { ...cache, assignedToMe: !cache.assignedToMe },
      },
    });

    get().fetchConversations(accountId, { force: true });
  },

  fetchAccountCounts: async () => {
    try {
      const counts = await getAccountConversationCounts();

      const unreadCounts: Record<string, number> = {};
      set((prev) => {
        const updatedCaches = { ...prev.accountCaches };

        for (const entry of counts) {
          unreadCounts[entry.account_id] = Number(entry.total_unread);

          const existingCache = getOrCreateCache(updatedCaches, entry.account_id);
          updatedCaches[entry.account_id] = {
            ...existingCache,
            statusCounts: {
              all: Number(entry.total_count),
              open: Number(entry.open_count),
              pending: Number(entry.pending_count),
              resolved: Number(entry.resolved_count),
            },
          };
        }

        return {
          accountUnreadCounts: unreadCounts,
          accountCaches: updatedCaches,
        };
      });
    } catch (error) {
      console.error('[InboxStore] Failed to fetch account counts:', error);
    }
  },

  // Phase 48 INBOX-03 — optimistic clear on conversation open.
  // Mirrors the increment branch in onMessageReceived (functional set-prev idiom).
  // Math.max(0, ...) floors at zero to defend against race conditions where
  // multiple decrements stack or `by` exceeds the current counter.
  decrementAccountUnread: (accountId, by) =>
    set((prev) => ({
      accountUnreadCounts: {
        ...prev.accountUnreadCounts,
        [accountId]: Math.max(0, (prev.accountUnreadCounts[accountId] || 0) - by),
      },
    })),

  onMessageReceived: (accountId, conversationId, message) => {
    const state = get();
    const cache = getOrCreateCache(state.accountCaches, accountId);
    const index = cache.conversations.findIndex((c) => c.id === conversationId);
    let clearedUnread = 0;

    if (index !== -1) {
      // Conversation exists in cache — update it
      const conversation = { ...cache.conversations[index] };
      conversation.last_message = message;
      // `sent_at` is the WhatsApp timestamp, `created_at` the row's. They only
      // differ for imported history, where created_at is backdated to the WA
      // time anyway — but sent_at is the field the list SORTS by, so read it
      // first and keep created_at as the fallback for rows that have none.
      conversation.last_message_at = message.sent_at ?? message.created_at;

      // Increment unread for incoming messages and update customer message timestamp
      if (message.direction === 'incoming') {
        conversation.unread_count = (conversation.unread_count || 0) + 1;
        conversation.last_customer_message_at = message.sent_at ?? message.created_at;
      } else if ((conversation.unread_count || 0) > 0) {
        // A reply typed on the phone: the agent has seen the thread there, so
        // it stops being "new" here too (the backend clears the counter on
        // ingest; this keeps the open inbox in step without a refetch).
        clearedUnread = conversation.unread_count;
        conversation.unread_count = 0;
      }

      // Move conversation to top of list
      const updatedConversations = [
        conversation,
        ...cache.conversations.filter((_, i) => i !== index),
      ];

      set({
        accountCaches: {
          ...state.accountCaches,
          [accountId]: { ...cache, conversations: updatedConversations },
        },
      });
    } else {
      // Conversation not in cache — fetch it asynchronously and prepend
      getConversation(conversationId)
        .then((fetchedConversation) => {
          set((prev) => {
            const prevCache = getOrCreateCache(prev.accountCaches, accountId);
            // Double-check it wasn't added while we were fetching
            if (prevCache.conversations.some((c) => c.id === conversationId)) {
              return prev;
            }
            return {
              accountCaches: {
                ...prev.accountCaches,
                [accountId]: {
                  ...prevCache,
                  conversations: [fetchedConversation, ...prevCache.conversations],
                },
              },
            };
          });
        })
        .catch((err) => {
          console.error(`[InboxStore] Failed to fetch new conversation ${conversationId}:`, err);
        });
    }

    // Increment account unread count for incoming messages
    if (message.direction === 'incoming') {
      set((prev) => ({
        accountUnreadCounts: {
          ...prev.accountUnreadCounts,
          [accountId]: (prev.accountUnreadCounts[accountId] || 0) + 1,
        },
      }));
    } else if (clearedUnread > 0) {
      get().decrementAccountUnread(accountId, clearedUnread);
    }
  },

  onMessageStatusUpdate: (accountId, whatsappMessageId, status, dbMessageId) => {
    set((prev) => {
      const cache = getOrCreateCache(prev.accountCaches, accountId);
      // Prefer matching by Laravel DB id when present (the 'sent' transition
      // emits this — the local message hasn't yet been told its WA id).
      // Fall back to WA id for delivered/read ACKs.
      const index = cache.conversations.findIndex((c) => {
        const lm = c.last_message;
        if (!lm) return false;
        if (dbMessageId !== undefined && lm.id === dbMessageId) return true;
        return lm.whatsapp_message_id === whatsappMessageId;
      });
      if (index === -1) return prev;

      const current = cache.conversations[index];
      // `read` on an INCOMING last message is the phone's own read receipt
      // (waxum read-self): the agent read the chat on the phone, so the thread
      // is no longer new here either. Outgoing reads are the customer's blue
      // ticks and leave the counter alone.
      const phoneRead = status === 'read'
        && current.last_message?.direction === 'incoming'
        && (current.unread_count || 0) > 0;
      const clearedUnread = phoneRead ? current.unread_count : 0;

      const updatedConversations = [...cache.conversations];
      updatedConversations[index] = {
        ...current,
        ...(phoneRead ? { unread_count: 0 } : {}),
        last_message: {
          ...current.last_message!,
          status,
          whatsapp_message_id: whatsappMessageId,
        },
      };

      return {
        accountCaches: {
          ...prev.accountCaches,
          [accountId]: { ...cache, conversations: updatedConversations },
        },
        ...(clearedUnread > 0
          ? {
              accountUnreadCounts: {
                ...prev.accountUnreadCounts,
                [accountId]: Math.max(0, (prev.accountUnreadCounts[accountId] || 0) - clearedUnread),
              },
            }
          : {}),
      };
    });
  },

  onConversationUpdated: (accountId, conversationId, updates) => {
    set((prev) => {
      const cache = getOrCreateCache(prev.accountCaches, accountId);
      const index = cache.conversations.findIndex((c) => c.id === conversationId);

      if (index === -1) return prev;

      const previous = cache.conversations[index];
      let updatedConversations = [...cache.conversations];
      updatedConversations[index] = { ...previous, ...updates };

      // Status transition: keep the segmented-row counts live and drop the
      // conversation from the pane when it no longer matches the active
      // status filter (e.g. Resolve while viewing "Open"). Filter "all"
      // never removes — which also keeps the mobile inbox (statusFilter
      // default "all") list-stable.
      let statusCounts = cache.statusCounts;
      if (updates.status && updates.status !== previous.status) {
        statusCounts = {
          ...statusCounts,
          [previous.status]: Math.max(0, (statusCounts[previous.status] ?? 0) - 1),
          [updates.status]: (statusCounts[updates.status] ?? 0) + 1,
        };
        if (cache.statusFilter !== 'all' && updates.status !== cache.statusFilter) {
          updatedConversations = updatedConversations.filter((c) => c.id !== conversationId);
        }
      }

      return {
        accountCaches: {
          ...prev.accountCaches,
          [accountId]: { ...cache, conversations: updatedConversations, statusCounts },
        },
      };
    });
  },

  resetStore: () => {
    set({
      accountCaches: {},
      activeAccountId: null,
      accountUnreadCounts: {},
    });
  },
}));
